import {
    ApiWithdrawReqSchema,
    ApiWithdrawResSchema,
    ApiWithdrawalsResSchema,
    type ApiWithdrawReq,
    type ApiWithdrawRes,
    type ApiWithdrawalsRes,
} from "@bridge-applications/pb-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { authenticatedApiCall } from "../helpers/api";
import i18n from "../i18n/i18n";

type WithdrawalsOk = Extract<ApiWithdrawalsRes, { status: "OK" }>;
type WithdrawOk = Extract<ApiWithdrawRes, { status: "OK" }>;

export class WithdrawalApiError extends Error {
    readonly response?: Extract<ApiWithdrawRes, { status: "ERROR" }>;

    constructor(
        message: string,
        response?: Extract<ApiWithdrawRes, { status: "ERROR" }>,
    ) {
        super(message);
        this.name = "WithdrawalApiError";
        this.response = response;
    }
}

function sameRequest(
    left: ApiWithdrawReq,
    right: Omit<ApiWithdrawReq, "requestId">,
) {
    return (
        left.currencyCode === right.currencyCode &&
        left.amount === right.amount &&
        left.destinationAddress === right.destinationAddress
    );
}

export function useWithdrawals(enabled: boolean) {
    const [data, setData] = useState<WithdrawalsOk>();
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState<unknown>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const retryRequestRef = useRef<ApiWithdrawReq | null>(null);
    const mountedRef = useRef(false);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const refetch = useCallback(async () => {
        if (!enabled) return null;
        if (mountedRef.current) {
            setIsLoading(true);
            setLoadError(null);
        }

        try {
            const { body } = await authenticatedApiCall(
                "/api/v1/withdrawals",
                ApiWithdrawalsResSchema,
            );
            if (body.status === "ERROR") {
                throw new WithdrawalApiError(
                    body.message ??
                        i18n.t("errors.loadWithdrawals", {
                            code: body.code,
                        }),
                );
            }
            if (mountedRef.current) setData(body);
            return body;
        } catch (error) {
            if (mountedRef.current) setLoadError(error);
            return null;
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    }, [enabled]);

    useEffect(() => {
        if (enabled) void refetch();
    }, [enabled, refetch]);

    const submit = useCallback(
        async (
            input: Omit<ApiWithdrawReq, "requestId">,
        ): Promise<WithdrawOk> => {
            const retained = retryRequestRef.current;
            const request = ApiWithdrawReqSchema.parse(
                retained && sameRequest(retained, input)
                    ? retained
                    : { ...input, requestId: crypto.randomUUID() },
            );
            retryRequestRef.current = request;
            if (mountedRef.current) setIsSubmitting(true);

            const call = () =>
                authenticatedApiCall(
                    "/api/v1/withdrawals",
                    ApiWithdrawResSchema,
                    {
                        method: "POST",
                        body: JSON.stringify(request),
                    },
                );

            try {
                let response;
                try {
                    response = await call();
                } catch {
                    response = await call();
                }

                const body = response.body;
                if (body.status === "ERROR") {
                    if (
                        body.code !== "UNKNOWN" &&
                        body.code !== "PERSON_VERIFICATION_REQUIRED"
                    ) {
                        retryRequestRef.current = null;
                    }
                    throw new WithdrawalApiError(
                        body.message ??
                            i18n.t("errors.withdrawalFailed", {
                                code: body.code,
                            }),
                        body,
                    );
                }

                retryRequestRef.current = null;
                if (mountedRef.current) {
                    setData((current) => {
                        if (!current) return current;
                        const withdrawals = current.withdrawals.filter(
                            (withdrawal) =>
                                withdrawal.id !== body.withdrawal.id,
                        );
                        return {
                            ...current,
                            withdrawals: [body.withdrawal, ...withdrawals],
                        };
                    });
                }
                return body;
            } finally {
                if (mountedRef.current) setIsSubmitting(false);
            }
        },
        [],
    );

    return {
        data,
        isLoading,
        loadError,
        refetch,
        submit,
        isSubmitting,
    };
}
